import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import config from '../../config';
import { getAdminUploadHeaders } from '../../utils/apiAuth';

const UploadCard = styled(Card)(({ theme }) => ({
  background: '#fff',
  borderRadius: '16px',
  border: '1px solid #e0e0e0',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const UploadButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
  color: 'white',
  padding: '12px 32px',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(45deg, #e85f2f 30%, #FFA726 70%)',
  },
  '&:disabled': {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.3)',
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Filename conventions for help popup
const FILENAME_CONVENTIONS = [
  { label: 'Senior this week', filename: 'thisweek_senior.jpeg' },
  { label: 'Senior 1 weeks old', filename: 'week1_senior.jpeg' },
  { label: 'Senior 2 weeks old', filename: 'week2_senior.jpeg' },
  { label: 'Junior this week', filename: 'thisweek_junior.jpeg' },
  { label: 'Junior 1 weeks old', filename: 'week1_junior.jpeg' },
  { label: 'Junior 2 weeks old', filename: 'week2_junior.jpeg' },
  { label: 'Beginner this week', filename: 'thisweek_beginner.jpeg' },
  { label: 'Beginner 1 weeks old', filename: 'week1_beginner.jpeg' },
  { label: 'Beginner 2 weeks old', filename: 'week2_beginner.jpeg' },
];

const FOLDER_OPTIONS = [
  { value: 'public_folder', label: 'public_folder' },
  { value: 'magazines', label: 'magazines' },
];

const S3ImageUploader = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [filename, setFilename] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [invalidationStatus, setInvalidationStatus] = useState('');
  const [invalidationId, setInvalidationId] = useState('');
  const [cdnEnabled, setCdnEnabled] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const apiBaseUrl = config.getApiUrl();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setFilename(file.name);
      setError('');
      setUploadedUrl('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!filename.trim()) {
      setError('Please enter a filename');
      return;
    }

    if (!selectedFolder) {
      setError('Please select a folder');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadStatus('Uploading to S3...');
    setInvalidationStatus('');
    setInvalidationId('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('filename', filename);
      formData.append('folder', selectedFolder);

      const response = await fetch(`${apiBaseUrl}/api/s3-upload`, {
        method: 'POST',
        headers: getAdminUploadHeaders(),
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadStatus('✓ S3 upload complete');
        
        // Check if CloudFront invalidation occurred
        if (data.data.cloudFrontInvalidationId) {
          setCdnEnabled(true);
          setInvalidationStatus('✓ CloudFront cache invalidated');
          setInvalidationId(data.data.cloudFrontInvalidationId);
        } else {
          setCdnEnabled(false);
          setInvalidationStatus('CloudFront not configured');
        }
        
        setUploadedUrl(data.data.url);
        setSuccess(data.message);
        setSelectedFile(null);
      } else {
        setUploadStatus('✗ Upload failed');
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus('✗ Upload failed');
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Cannot connect to server. Please ensure the backend is running on ' + apiBaseUrl);
      } else {
        setError('Failed to upload file. Please try again. Error: ' + err.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(uploadedUrl);
    setSnackbarMessage('URL copied to clipboard!');
    setShowSnackbar(true);
  };

  const handleCopyFilename = (filename) => {
    navigator.clipboard.writeText(filename);
    setSnackbarMessage(`Copied: ${filename}`);
    setShowSnackbar(true);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setFilename('');
    setSelectedFolder('');
    setUploadedUrl('');
    setError('');
    setSuccess('');
    setUploadStatus('');
    setInvalidationStatus('');
    setInvalidationId('');
    setCdnEnabled(false);
  };

  return (
    <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
      <Typography variant="h5" sx={{ color: '#333', mb: 3, fontWeight: 'bold' }}>
        S3 Image Uploader
      </Typography>

      <UploadCard>
        <CardContent sx={{ position: 'relative' }}>
          {/* Help Icon in top right */}
          <Tooltip title="View filename conventions">
            <IconButton
              onClick={() => setShowHelpDialog(true)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: '#FF6B35',
                '&:hover': {
                  background: 'rgba(255, 107, 53, 0.1)',
                },
              }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Upload images to S3 bucket. If a file with the same name exists, it will be overwritten.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{
                color: '#FF6B35',
                borderColor: '#FF6B35',
                '&:hover': {
                  borderColor: '#e85f2f',
                  background: 'rgba(255, 107, 53, 0.1)',
                },
              }}
            >
              Select Image File
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            {selectedFile && (
              <Typography sx={{ color: '#666', mt: 1, fontSize: '0.9rem' }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>

          <FormControl
            fullWidth
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#333',
                '& fieldset': {
                  borderColor: '#ccc',
                },
                '&:hover fieldset': {
                  borderColor: '#FF6B35',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FF6B35',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#666',
              },
            }}
          >
            <InputLabel>Select Folder *</InputLabel>
            <Select
              value={selectedFolder}
              onChange={(e) => setSelectedFolder(e.target.value)}
              label="Select Folder *"
            >
              {FOLDER_OPTIONS.map((folder) => (
                <MenuItem key={folder.value} value={folder.value}>
                  {folder.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Filename (e.g., carousel-1.jpg)"
            variant="outlined"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder="carousel-1.jpg"
            helperText="This will be the final filename in S3. Include the file extension."
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#333',
                '& fieldset': {
                  borderColor: '#ccc',
                },
                '&:hover fieldset': {
                  borderColor: '#FF6B35',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#FF6B35',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#666',
              },
              '& .MuiFormHelperText-root': {
                color: '#999',
              },
            }}
          />

          {uploading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Upload and Invalidation Status */}
          {(uploadStatus || invalidationStatus) && (
            <Box sx={{ mb: 2, p: 2, background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
              {uploadStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: invalidationStatus ? 1 : 0 }}>
                  <Typography 
                    sx={{ 
                      color: uploadStatus.includes('✓') ? '#4CAF50' : uploadStatus.includes('✗') ? '#f44336' : '#FF6B35',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {uploadStatus}
                  </Typography>
                </Box>
              )}
              {invalidationStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography 
                    sx={{ 
                      color: invalidationStatus.includes('✓') ? '#4CAF50' : '#999',
                      fontSize: '0.9rem',
                      fontWeight: cdnEnabled ? 'bold' : 'normal'
                    }}
                  >
                    {invalidationStatus}
                  </Typography>
                  {invalidationId && (
                    <Typography 
                      sx={{ 
                        color: '#666',
                        fontSize: '0.75rem',
                        fontStyle: 'italic'
                      }}
                    >
                      (ID: {invalidationId})
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <UploadButton
              onClick={handleUpload}
              disabled={!selectedFile || !filename.trim() || !selectedFolder || uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload to S3'}
            </UploadButton>

            {(selectedFile || uploadedUrl) && (
              <Button
                onClick={handleReset}
                variant="outlined"
                sx={{
                  color: '#666',
                  borderColor: '#ccc',
                  '&:hover': {
                    borderColor: '#999',
                  },
                }}
              >
                Reset
              </Button>
            )}
          </Box>

          {uploadedUrl && (
            <Box sx={{ mt: 3, p: 2, background: 'rgba(76, 175, 80, 0.1)', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                <Typography sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                  Upload Successful!
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  value={uploadedUrl}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    sx: {
                      color: '#333',
                      fontSize: '0.9rem',
                      '& fieldset': {
                        borderColor: '#4CAF50',
                      },
                    },
                  }}
                />
                <IconButton
                  onClick={handleCopyUrl}
                  sx={{
                    color: '#4CAF50',
                    '&:hover': {
                      background: 'rgba(76, 175, 80, 0.2)',
                    },
                  }}
                  title="Copy URL"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </CardContent>
      </UploadCard>

      <Box sx={{ p: 2, background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
        <Typography variant="body2" sx={{ color: '#333', mb: 1, fontWeight: 'bold' }}>
          📝 Instructions:
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
          1. Select an image file (max 10MB)<br />
          2. Enter the desired filename (e.g., carousel-1.jpg)<br />
          3. Click "Upload to S3"<br />
          4. If a file with the same name exists, it will be overwritten<br />
          5. Copy the URL to use in your application
        </Typography>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Help Dialog with Filename Conventions */}
      <Dialog
        open={showHelpDialog}
        onClose={() => setShowHelpDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#333', fontWeight: 'bold', borderBottom: '2px solid #FF6B35' }}>
          📝 Filename Conventions
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Click the copy icon to copy the filename to your clipboard:
          </Typography>
          <List>
            {FILENAME_CONVENTIONS.map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  mb: 1,
                  '&:hover': {
                    background: 'rgba(255, 107, 53, 0.05)',
                  },
                }}
                secondaryAction={
                  <Tooltip title="Copy filename">
                    <IconButton
                      edge="end"
                      onClick={() => handleCopyFilename(item.filename)}
                      sx={{
                        color: '#FF6B35',
                        '&:hover': {
                          background: 'rgba(255, 107, 53, 0.1)',
                        },
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Typography sx={{ color: '#333', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {item.label}
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ color: '#666', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                      {item.filename}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setShowHelpDialog(false)}
            sx={{
              color: '#FF6B35',
              '&:hover': {
                background: 'rgba(255, 107, 53, 0.1)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default S3ImageUploader;
